import React, { useMemo, useRef, useEffect, useState } from 'react';
import { Star, Check, Dumbbell, Gift } from 'lucide-react';

const StartBubble = () => (
  <div className="start-bubble" style={{
    position: 'absolute',
    top: '-55px',
    left: '50%',
    zIndex: 20,
    pointerEvents: 'none'
  }}>
    <div style={{
      backgroundColor: '#ffffff',
      border: '2px solid #1cb0f6',
      color: '#1cb0f6',
      padding: '8px 12px',
      borderRadius: '12px',
      fontWeight: '800',
      fontSize: '0.85rem',
      boxShadow: '0 4px 0 rgba(0,0,0,0.05)',
      position: 'relative',
      textTransform: 'uppercase',
      whiteSpace: 'nowrap'
    }}>
      ANFANGEN
      <div style={{
        position: 'absolute',
        bottom: '-7px',
        left: '50%',
        marginLeft: '-6px',
        width: '12px',
        height: '12px',
        backgroundColor: '#ffffff',
        borderBottom: '2px solid #1cb0f6',
        borderRight: '2px solid #1cb0f6',
        transform: 'rotate(45deg)'
      }}></div>
    </div>
  </div>
);

const DolphinAvatar = () => {
  const [mood, setMood] = useState('idle');

  useEffect(() => {
    let idleTimer;
    const resetIdle = () => {
      setMood('idle');
      clearTimeout(idleTimer);
      // Nach 5 Sekunden Inaktivität eine zufällige Aktion starten
      idleTimer = setTimeout(() => {
        const actions = ['spin'];
        setMood(actions[Math.floor(Math.random() * actions.length)]);
      }, 5000);
    };

    window.addEventListener('mousemove', resetIdle);
    window.addEventListener('click', resetIdle);
    window.addEventListener('keydown', resetIdle);
    
    resetIdle();

    return () => {
      window.removeEventListener('mousemove', resetIdle);
      window.removeEventListener('click', resetIdle);
      window.removeEventListener('keydown', resetIdle);
      clearTimeout(idleTimer);
    };
  }, []);

  const getContent = () => {
    return '🐬';
  };

  return (
    <div className={`avatar-dolphin ${mood}`} style={{
      position: 'absolute',
      top: '50%',
      left: '-70px',
      fontSize: '45px',
      zIndex: 10,
      filter: 'drop-shadow(0 4px 4px rgba(0,0,0,0.2))'
    }}>{getContent()}</div>
  );
};

const SectionDivider = ({ text }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '40px 0 20px 0', position: 'relative', zIndex: 1 }}>
    <div style={{ height: '2px', backgroundColor: '#e5e5e5', flex: 1, maxWidth: '150px' }}></div>
    <span style={{ margin: '0 15px', color: '#afafaf', fontWeight: 'bold', fontSize: '1.1rem', textTransform: 'uppercase' }}>{text}</span>
    <div style={{ height: '2px', backgroundColor: '#e5e5e5', flex: 1, maxWidth: '150px' }}></div>
  </div>
);

const AnimatedLevelNode = ({ children, style, delay }) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      style={style}
    >
      {typeof children === 'function' ? children(isVisible) : (
        <div style={{
          width: '100%', height: '100%',
          opacity: isVisible ? undefined : 0,
          animation: isVisible ? `pop-up 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275) ${delay} both` : 'none'
        }}>
          {children}
        </div>
      )}
    </div>
  );
};

function MainContent({ completedLessons, onStartLesson, collectedLootboxes = [], onCollectLootbox, onSectionColorChange, activeCourse = 'Python' }) {
  const containerRef = useRef(null);
  const sectionRefs = useRef({});

  const pythonSections = [
    {
      id: 1,
      header: "STUFE 1, ABSCHNITT 1",
      title: "Einführung in Python",
      color: "#1cb0f6",
      shadow: "#148ac2",
      levels: Array.from({ length: 10 }, (_, i) => i + 1),
      lootboxes: [3, 10]
    },
    {
      id: 2,
      header: "STUFE 1, ABSCHNITT 2",
      title: "Python Basics",
      color: "#ff86d0",
      shadow: "#c95d9e",
      levels: Array.from({ length: 10 }, (_, i) => i + 11),
      lootboxes: [13, 20]
    },
    {
      id: 3,
      header: "STUFE 1, ABSCHNITT 3",
      title: "Vergleiche & Typen",
      color: "#58CC02",
      shadow: "#46a302",
      levels: Array.from({ length: 10 }, (_, i) => i + 21),
      lootboxes: [24, 29]
    }
    ,
    {
      id: 4,
      header: "STUFE 2, ABSCHNITT 1",
      title: "Conditional Statements",
      color: "#FF9600",
      shadow: "#e58700",
      levels: Array.from({ length: 10 }, (_, i) => i + 31),
      lootboxes: [33, 40]
    },
    {
      id: 5,
      header: "STUFE 2, ABSCHNITT 2",
      title: "Else Statements",
      color: "#CC348D",
      shadow: "#a32a71",
      levels: Array.from({ length: 10 }, (_, i) => i + 51),
      lootboxes: [53, 60]
    },
    {
      id: 6,
      header: "STUFE 2, ABSCHNITT 3",
      title: "Elif & Logik Operatoren",
      color: "#DC8F47",
      shadow: "#b86f28",
      levels: Array.from({ length: 10 }, (_, i) => i + 61),
      lootboxes: [63, 70]
    },
    {
      id: 7,
      header: "STUFE 3, ABSCHNITT 1",
      title: "Schleifen & Zuweisung",
      color: "#CE82FF",
      shadow: "#a568cc",
      levels: Array.from({ length: 10 }, (_, i) => i + 71),
      lootboxes: [73, 80]
    },
    {
      id: 8,
      header: "STUFE 3, ABSCHNITT 2",
      title: "Schleifen Steuerung",
      color: "#00CD9C",
      shadow: "#00a37a",
      levels: Array.from({ length: 10 }, (_, i) => i + 81),
      lootboxes: [83, 90]
    },
    {
      id: 9,
      header: "STUFE 3, ABSCHNITT 3",
      title: "For Schleifen",
      color: "#1CB0F6",
      shadow: "#148ac2",
      levels: Array.from({ length: 10 }, (_, i) => i + 91),
      lootboxes: [93, 100]
    },
    {
      id: 10,
      header: "STUFE 4, ABSCHNITT 1",
      title: "Listen & Indizes",
      color: "#FF86D0",
      shadow: "#c95d9e",
      levels: Array.from({ length: 10 }, (_, i) => i + 101),
      lootboxes: [103, 110]
    },
    {
      id: 11,
      header: "STUFE 4, ABSCHNITT 2",
      title: "Listen & Schleifen",
      color: "#58CC02",
      shadow: "#46a302",
      levels: Array.from({ length: 10 }, (_, i) => i + 111),
      lootboxes: [113, 120]
    },
    {
      id: 12,
      header: "STUFE 4, ABSCHNITT 3",
      title: "Listen Auswertung",
      color: "#FF9600",
      shadow: "#e58700",
      levels: Array.from({ length: 10 }, (_, i) => i + 121),
      lootboxes: [123, 130]
    },
    {
      id: 13,
      header: "STUFE 4, ABSCHNITT 4",
      title: "Listen verbinden & zählen",
      color: "#CC348D",
      shadow: "#a32a71",
      levels: Array.from({ length: 10 }, (_, i) => i + 131),
      lootboxes: [133, 140]
    },
    {
      id: 14,
      header: "STUFE 5, ABSCHNITT 1",
      title: "Strings & Operationen",
      color: "#DC8F47",
      shadow: "#b86f28",
      levels: Array.from({ length: 10 }, (_, i) => i + 141),
      lootboxes: [143, 150]
    },
    {
      id: 15,
      header: "STUFE 6, ABSCHNITT 1",
      title: "Funktionen & Parameter",
      color: "#CE82FF",
      shadow: "#a568cc",
      levels: Array.from({ length: 10 }, (_, i) => i + 151),
      lootboxes: [153, 160]
    }
  ];

  const javascriptSections = [
    {
      id: 1,
      header: "STUFE 1, ABSCHNITT 1",
      title: "Einführung in JavaScript",
      color: "#1CB0F6",
      shadow: "#148ac2",
      levels: Array.from({ length: 10 }, (_, i) => i + 1),
      lootboxes: [3, 10]
    },
    {
      id: 2,
      header: "STUFE 1, ABSCHNITT 2",
      title: "JavaScript Basics",
      color: "#FF86D0",
      shadow: "#c95d9e",
      levels: Array.from({ length: 10 }, (_, i) => i + 11),
      lootboxes: [13, 20]
    },
    {
      id: 3,
      header: "STUFE 1, ABSCHNITT 3",
      title: "Zahlen Vergleiche",
      color: "#58CC02",
      shadow: "#46a302",
      levels: Array.from({ length: 10 }, (_, i) => i + 21),
      lootboxes: [23, 30]
    },
    {
      id: 4,
      header: "STUFE 2, ABSCHNITT 1",
      title: "Logik & Typen",
      color: "#FF9600",
      shadow: "#e58700",
      levels: Array.from({ length: 10 }, (_, i) => i + 31),
      lootboxes: [33, 40]
    },
    {
      id: 5,
      header: "STUFE 3, ABSCHNITT 1",
      title: "if/else Anweisungen",
      color: "#CC348D",
      shadow: "#a32a71",
      levels: Array.from({ length: 10 }, (_, i) => i + 41),
      lootboxes: [43, 50]
    },
    {
      id: 6,
      header: "STUFE 3, ABSCHNITT 2",
      title: "Erweiterte Bedingungen",
      color: "#DC8F47",
      shadow: "#b07239",
      levels: Array.from({ length: 10 }, (_, i) => i + 51),
      lootboxes: [53, 60]
    },
    {
      id: 7,
      header: "STUFE 4, ABSCHNITT 1",
      title: "Zuweisung & Schleifen",
      color: "#CE82FF",
      shadow: "#a568cc",
      levels: Array.from({ length: 10 }, (_, i) => i + 61),
      lootboxes: [63, 70]
    },
    {
      id: 8,
      header: "STUFE 4, ABSCHNITT 2",
      title: "Fortgeschrittene Schleifen",
      color: "#00CD9C",
      shadow: "#00a37a",
      levels: Array.from({ length: 10 }, (_, i) => i + 71),
      lootboxes: [73, 80]
    },
    {
      id: 9,
      header: "STUFE 4, ABSCHNITT 3",
      title: "For Schleifen",
      color: "#1CB0F6",
      shadow: "#148ac2",
      levels: Array.from({ length: 10 }, (_, i) => i + 81),
      lootboxes: [83, 90]
    },
    {
      id: 10,
      header: "STUFE 5, ABSCHNITT 1",
      title: "Arrays & Listen",
      color: "#FF86D0",
      shadow: "#c76ba6",
      levels: Array.from({ length: 10 }, (_, i) => i + 91),
      lootboxes: [93, 100]
    },
    {
      id: 11,
      header: "STUFE 5, ABSCHNITT 2",
      title: "Array Methoden",
      color: "#58CC02",
      shadow: "#46a302",
      levels: Array.from({ length: 10 }, (_, i) => i + 101),
      lootboxes: [103, 110]
    },
    {
      id: 12,
      header: "STUFE 6, ABSCHNITT 1",
      title: "Funktionen & Parameter",
      color: "#FF9600",
      shadow: "#e58700",
      levels: Array.from({ length: 10 }, (_, i) => i + 111),
      lootboxes: [113, 120]
    }
  ];

  const htmlSections = [
    {
      id: 1,
      header: "STUFE 1, ABSCHNITT 1",
      title: "Einführung in HTML",
      color: "#1CB0F6",
      shadow: "#148ac2",
      levels: Array.from({ length: 10 }, (_, i) => i + 1),
      lootboxes: [3, 10]
    },
    {
      id: 2,
      header: "STUFE 1, ABSCHNITT 2",
      title: "Links, Bilder & Attribute",
      color: "#FF86D0",
      shadow: "#c95d9e",
      levels: Array.from({ length: 10 }, (_, i) => i + 11),
      lootboxes: [13, 20]
    },
    {
      id: 3,
      header: "STUFE 2, ABSCHNITT 1",
      title: "Formulare & Container",
      color: "#58CC02",
      shadow: "#46a302",
      levels: Array.from({ length: 10 }, (_, i) => i + 21),
      lootboxes: [23, 30]
    },
    {
      id: 4,
      header: "STUFE 2, ABSCHNITT 2",
      title: "Listen & Navigation",
      color: "#FF9600",
      shadow: "#e58700",
      levels: Array.from({ length: 10 }, (_, i) => i + 31),
      lootboxes: [33, 40]
    },
    {
      id: 5,
      header: "STUFE 3, ABSCHNITT 1",
      title: "Semantisches HTML",
      color: "#CC348D",
      shadow: "#a32a71",
      levels: Array.from({ length: 10 }, (_, i) => i + 41),
      lootboxes: [43, 50]
    },
    {
      id: 6,
      header: "STUFE 3, ABSCHNITT 2",
      title: "Semantische Container & Aussehen",
      color: "#DC8F47",
      shadow: "#b07239",
      levels: Array.from({ length: 10 }, (_, i) => i + 51),
      lootboxes: [53, 60]
    },
    {
      id: 7,
      header: "STUFE 4, ABSCHNITT 1",
      title: "Barrierefreiheit Grundlagen",
      color: "#CE82FF",
      shadow: "#a568cc",
      levels: Array.from({ length: 10 }, (_, i) => i + 61),
      lootboxes: [63, 70]
    },
    {
      id: 8,
      header: "STUFE 5, ABSCHNITT 1",
      title: "HTML Formulare",
      color: "#00CD9C",
      shadow: "#00a37a",
      levels: Array.from({ length: 10 }, (_, i) => i + 71),
      lootboxes: [73, 80]
    }
  ];

  const kotlinSections = [
    {
      id: 1,
      header: "STUFE 1, ABSCHNITT 1",
      title: "Einführung in Kotlin",
      color: "#1cb0f6",
      shadow: "#148ac2",
      levels: Array.from({ length: 10 }, (_, i) => i + 1),
      lootboxes: [3, 10]
    },
    {
      id: 2,
      header: "STUFE 1, ABSCHNITT 2",
      title: "Variablen, Operatoren & Comments",
      color: "#FF86D0",
      shadow: "#c95d9e",
      levels: Array.from({ length: 10 }, (_, i) => i + 11),
      lootboxes: [15, 20]
    },
    {
      id: 3,
      header: "STUFE 2, ABSCHNITT 1",
      title: "Input & If Statements",
      color: "#58CC02",
      shadow: "#46a302",
      levels: Array.from({ length: 10 }, (_, i) => i + 21),
      lootboxes: [23, 30]
    },
    {
      id: 4,
      header: "STUFE 2, ABSCHNITT 2",
      title: "Else Statements",
      color: "#FF9600",
      shadow: "#e58700",
      levels: Array.from({ length: 10 }, (_, i) => i + 31),
      lootboxes: [33, 40]
    },
    {
      id: 5,
      header: "STUFE 2, ABSCHNITT 3",
      title: "Conditional Expressions",
      color: "#CC348D",
      shadow: "#a32a71",
      levels: Array.from({ length: 10 }, (_, i) => i + 41),
      lootboxes: [43, 50]
    },
    {
      id: 6,
      header: "STUFE 3, ABSCHNITT 1",
      title: "Schleifen & Zuweisung",
      color: "#DC8F47",
      shadow: "#b86f28",
      levels: Array.from({ length: 10 }, (_, i) => i + 51),
      lootboxes: [53, 60]
    },
    {
      id: 7,
      header: "STUFE 4, ABSCHNITT 1",
      title: "Arrays & Listen",
      color: "#CE82FF",
      shadow: "#a568cc",
      levels: Array.from({ length: 10 }, (_, i) => i + 61),
      lootboxes: [63, 70]
    },
    {
      id: 8,
      header: "STUFE 5, ABSCHNITT 1",
      title: "For Schleifen",
      color: "#00CD9C",
      shadow: "#00a37a",
      levels: Array.from({ length: 10 }, (_, i) => i + 71),
      lootboxes: [73, 80]
    },
    {
      id: 9,
      header: "STUFE 5, ABSCHNITT 2",
      title: "Ranges",
      color: "#1CB0F6",
      shadow: "#148ac2",
      levels: Array.from({ length: 10 }, (_, i) => i + 81),
      lootboxes: [83, 90]
    },
    {
      id: 10,
      header: "STUFE 6, ABSCHNITT 1",
      title: "Funktionen & Parameter",
      color: "#FF86D0",
      shadow: "#c95d9e",
      levels: Array.from({ length: 10 }, (_, i) => i + 91),
      lootboxes: [93, 100]
    },
    {
      id: 11,
      header: "STUFE 6, ABSCHNITT 2",
      title: "Return & Lambdas",
      color: "#58CC02",
      shadow: "#46a302",
      levels: Array.from({ length: 10 }, (_, i) => i + 101),
      lootboxes: [103, 110]
    },
    {
      id: 12,
      header: "STUFE 6, ABSCHNITT 3",
      title: "Higher-Order Functions",
      color: "#FF9600",
      shadow: "#cc7a00",
      levels: Array.from({ length: 10 }, (_, i) => i + 111),
      lootboxes: [113, 120]
    },
    {
      id: 13,
      header: "STUFE 7, ABSCHNITT 1",
      title: "Classes & Objects",
      color: "#CC348D",
      shadow: "#a32a71",
      levels: Array.from({ length: 10 }, (_, i) => i + 121),
      lootboxes: [123, 130]
    },
    {
      id: 14,
      header: "STUFE 7, ABSCHNITT 2",
      title: "Getters & Setters",
      color: "#DC8F47",
      shadow: "#b86f28",
      levels: Array.from({ length: 10 }, (_, i) => i + 131),
      lootboxes: [133, 140]
    }
  ];

  let sections;
  if (activeCourse === 'JavaScript') {
    sections = javascriptSections;
  } else if (activeCourse === 'HTML') {
    sections = htmlSections;
  } else if (activeCourse === 'Kotlin') {
    sections = kotlinSections;
  } else {
    sections = pythonSections;
  }

  const handleScroll = () => {
    if (!containerRef.current) return;
    
    // Find the last section that is above the middle of the screen
    let currentSection = sections[0];
    for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        const el = sectionRefs.current[section.id];
        if (el && el.getBoundingClientRect().top < window.innerHeight / 2) {
            currentSection = section;
            break;
        }
    }

    if (currentSection && onSectionColorChange) {
        onSectionColorChange(currentSection.color);
    }
  };

  return (
    <main className="main-content" ref={containerRef} style={{ position: 'relative', height: '100vh', overflow: 'hidden' }}>
      <div className="scroll-container" onScroll={handleScroll} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', overflowX: 'hidden', overflowY: 'auto', zIndex: 1, padding: '20px', boxSizing: 'border-box' }}>
        <div style={{ position: 'relative', zIndex: 1, paddingBottom: '50px' }}>
          {sections.map((section, sectionIndex) => (
            <div key={section.id} ref={el => sectionRefs.current[section.id] = el}>
              {sectionIndex > 0 && <SectionDivider text={`ABSCHNITT ${section.id}`} />}
              
              <div className="main-content-header" style={{ position: 'sticky', top: '20px', zIndex: 10, backgroundColor: section.color, borderBottom: `4px solid ${section.shadow}`, borderRadius: '16px', color: '#ffffff' }}>
                <h2 style={{ color: 'rgba(255, 255, 255, 0.7)' }}>{section.header}</h2>
                <span style={{ color: '#ffffff' }}>{section.title}</span>
              </div>
              
              <div className="level-path" style={{ position: 'relative', marginTop: '40px', display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
                  {section.levels.map((level, index) => {
                    const lessonId = `${activeCourse}_${level}`;
                    const isCompleted = completedLessons.includes(lessonId);
                    // Kurve: Erst links (negativ), dann rechts. Sinus-Funktion für den Schwung.
                    const offset = -Math.sin(index * 0.7) * 160; 
                    const isLootbox = section.lootboxes.includes(level);
                    const isLocked = level !== 1 && !completedLessons.includes(`${activeCourse}_${level - 1}`);
                    const isCollected = collectedLootboxes.includes(lessonId);
                    
                    // Aktuelles Level ermitteln (das erste nicht abgeschlossene)
                    const isCurrent = !isCompleted && (level === 1 || completedLessons.includes(`${activeCourse}_${level - 1}`));

                    const handleClick = () => {
                      if (navigator.vibrate) navigator.vibrate(10);
                      if (!isLocked) {
                        if (isLootbox) {
                          if (!isCollected) onCollectLootbox(lessonId);
                        } else {
                          onStartLesson(lessonId);
                        }
                      }
                    };

                    const animationDelay = `${index * 0.1}s`;

                    // Dynamic styles for colors
                    const nodeStyle = {
                      '--level-color': section.color,
                      '--level-shadow': section.shadow
                    };

                    if (isLootbox) {
                      return (
                        <AnimatedLevelNode 
                          key={level} 
                          delay={animationDelay}
                          style={{ 
                            marginLeft: `${offset}px`,
                            position: 'relative',
                            width: '80px',
                            height: '65px',
                            zIndex: 1
                          }}
                        >
                          <div className={`level-node lootbox ${isLocked ? 'locked' : ''}`} onClick={handleClick} style={{ width: '100%', height: '100%', opacity: isCollected ? 0.5 : 1, cursor: isCollected ? 'default' : 'pointer' }}>
                            {isCollected ? (
                              <Check className="level-icon" size={32} strokeWidth={4} />
                            ) : (
                              <Gift className="level-icon" size={32} strokeWidth={3} />
                            )}
                          </div>
                        </AnimatedLevelNode>
                      );
                    }
                    
                    return (
                      <AnimatedLevelNode 
                        key={level}
                        delay={animationDelay}
                        style={{ 
                          marginLeft: `${offset}px`, 
                          position: 'relative', 
                          width: '80px', 
                          height: '65px'
                        }}
                      >
                        {(isVisible) => {
                          const animStyle = {
                            opacity: isVisible ? undefined : 0,
                            animation: isVisible ? `pop-up 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275) ${animationDelay} both` : 'none'
                          };
                          return (
                            <>
                              {isCurrent && (
                                <div style={{ ...animStyle, position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 20, pointerEvents: 'none' }}>
                                  <StartBubble />
                                </div>
                              )}
                              <div style={{ ...animStyle, width: '100%', height: '100%', zIndex: 1, position: 'relative' }}>
                                {isCurrent && <DolphinAvatar />}
                                <div 
                                  className={`level-node ${isCompleted ? 'completed' : (isLocked ? 'locked' : '')}`}
                                  onClick={handleClick}
                                  style={{ width: '100%', height: '100%', ...nodeStyle }}
                                >
                                {isCompleted ? (
                                  <Check className="level-icon" size={32} strokeWidth={4} />
                                ) : isLocked ? (
                                  <Dumbbell className="level-icon" size={32} strokeWidth={3} />
                                ) : (
                                  <Star className="level-icon" size={32} strokeWidth={3} />
                                )}
                                </div>
                              </div>
                            </>
                          );
                        }}
                      </AnimatedLevelNode>
                    );
                  })}
              </div>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        .scroll-container::-webkit-scrollbar {
          display: none;
        }
        .scroll-container {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }

        .avatar-dolphin.idle { animation: bounce 1.5s infinite; }
        .avatar-dolphin.spin { animation: spin 1s infinite linear; }
        .start-bubble { animation: float-bubble 2s ease-in-out infinite; }
        @keyframes float-bubble {
          0%, 100% { transform: translateX(-50%) translateY(0); }
          50% { transform: translateX(-50%) translateY(-10px); }
        }
        @keyframes bounce { 0%, 100% { transform: translateY(-50%); } 50% { transform: translateY(-70%); } }
        @keyframes spin { from { transform: translateY(-50%) rotate(0deg); } to { transform: translateY(-50%) rotate(360deg); } }
        @keyframes pop-up {
          0% { transform: scale(0); opacity: 0; }
          100% { transform: scale(1); opacity: 1; }
        }
        
        /* Glow Effect Styles */
        .level-node {
          width: 80px;
          height: 65px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          cursor: pointer;
          background-color: var(--level-color, #1cb0f6);
          box-shadow: 0 6px 0 var(--level-shadow, #148ac2), 0 8px 10px rgba(0,0,0,0.2);
          transition: transform 0.1s, box-shadow 0.1s;
          color: white;
          border: none;
          overflow: hidden;
        }
        
        /* Highlight/Glow */
        .level-node::before, .level-node::after {
          content: '';
          position: absolute;
          pointer-events: none;
        }
        
        .level-node::before {
          top: -5px;
          left: 10px;
          width: 15px;
          height: 50px;
          border-radius: 100% 0 0 100% / 50% 0 0 50%;
          transform: rotate(45deg);
          background: rgba(255,255,255,0.4);
        }

        .level-node::after {
          top: 28px;
          right: 5px;
          width: 65px;
          height: 8px;
          border-radius: 20px;
          transform: rotate(-45deg);
          background: rgba(255,255,255,0.2);
        }

        .level-node:active {
          transform: translateY(6px);
          box-shadow: 0 0 0 var(--level-shadow, #148ac2);
        }

        .level-node.completed {
          background-color: var(--level-color, #1cb0f6);
          box-shadow: 0 6px 0 var(--level-shadow, #148ac2), 0 8px 10px rgba(0,0,0,0.2);
        }
        .level-node.completed:active {
          box-shadow: 0 0 0 var(--level-shadow, #148ac2);
        }

        .level-node.lootbox {
          background-color: #ffc800;
          box-shadow: 0 6px 0 #e5a500, 0 8px 10px rgba(0,0,0,0.2);
        }
        .level-node.lootbox:active {
          box-shadow: 0 0 0 #e5a500;
        }

        .level-node.locked {
          background-color: #afafaf;
          box-shadow: 0 6px 0 #8f8f8f, 0 8px 10px rgba(0,0,0,0.2);
          color: white;
          cursor: default;
        }
        .level-node.locked:active {
          transform: none;
          box-shadow: 0 6px 0 #8f8f8f, 0 8px 10px rgba(0,0,0,0.2);
        }
        
        .level-icon {
            z-index: 2;
            filter: drop-shadow(0 2px 0 rgba(0,0,0,0.1));
        }
        
        /* Stronger shadow for locked icon to be visible on gray */
        .level-node.locked .level-icon {
            filter: drop-shadow(0 2px 0 rgba(0,0,0,0.2));
        }
      `}</style>
    </main>
  );
}

export default MainContent;